import { Skeleton } from '@mui/lab';
import { Grid } from '@mui/material';
import { Field, Formik, FormikErrors } from 'formik';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import {
	CerebroDrawer,
	CerebroDropdown,
	CerebroDropdownMenuItem,
	CerebroForm,
	CerebroHeading,
	CerebroHierarchyFolderTree,
	CerebroInput,
	CerebroLabel,
	CerebroPrimaryButton,
	CerebroStyledBox,
	CerebroText,
	CerebroTextarea,
	CerebroYesNo,
} from '../../shared/components';
import { useApi, useAuth } from '../../shared/context';
import {
	QueryKeys,
	useActiveDataRoomUsersQuery,
	useDataRoomFolderHierarchyQuery,
	useGetDataRoomUserByUserQuery,
} from '../../shared/hooks';
import { DataRoomDiligenceRequest, DiligenceRequestType } from '../../shared/models';
import { FormikUtils } from '../../shared/utils';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getApiResponseData } from '../../shared/utils/axios';

interface AddQuestionFormProps {
	dataRoomGuid: string | undefined;
	open: boolean;
	close: () => void;
}

export const AddQuestionForm: React.FC<AddQuestionFormProps> = ({ dataRoomGuid, open, close }) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();
	var { currentUser } = useAuth();

	const lenderQuery = useActiveDataRoomUsersQuery(dataRoomGuid);
	const folderQuery = useDataRoomFolderHierarchyQuery(dataRoomGuid);
	const roomUserQuery = useGetDataRoomUserByUserQuery(dataRoomGuid, currentUser.userGuid, true, {
		enabled: currentUser.isBankUser,
		retry: false,
	});

	const addRequestMutation = useMutation(
		async ({ values }: AddQuestionFormMutationVariables) => {
			const { data, error } = await getApiResponseData<DataRoomDiligenceRequest>(
				api.post(CerebroApiEndpoints.DataRoomDiligenceRequests.Root(dataRoomGuid), values)
			);

			if (error) {
				throw new Error(error.response?.data);
			}

			return data;
		},
		{
			onSuccess: () => {
				if (currentUser.isBankUser) {
					client.invalidateQueries(QueryKeys.DataRoomDiligenceRequests.Lender(dataRoomGuid));
				} else {
					client.invalidateQueries(QueryKeys.DataRoomDiligenceRequests.Root(dataRoomGuid));
				}
				snackbar.enqueueSnackbar('Diligence request was created.', { variant: 'success' });
				close();
			},
			onError: (error: Error) => {
				snackbar.enqueueSnackbar(error.message, { variant: 'error' });
			},
			onSettled: (_data, _error, { helpers }) => {
				helpers.setSubmitting(false);
			},
		}
	);

	return (
		<CerebroDrawer open={open} onClose={close}>
			<CerebroHeading size="h1" component="h1">
				Add Question
			</CerebroHeading>

			<Formik
				initialValues={{
					lenderGuid: currentUser.isBankUser ? roomUserQuery?.data?.guid : undefined,
					title: '',
					diligenceType: 'Question' as DiligenceRequestType,
					details: '',
					folderGuid: folderQuery.data?.guid ?? '',
					sendEmail: true,
				}}
				validate={validateForm}
				onSubmit={FormikUtils.MutateOnSubmit(addRequestMutation.mutate)}
			>
				{({ isSubmitting, errors, touched, handleSubmit, values, setFieldValue }) => {
					return (
						<CerebroForm onSubmit={handleSubmit}>
							<Grid
								container
								spacing={3}
								sx={{ marginTop: 3 }}
								justifyContent={{ xs: 'center', md: 'flex-start' }}
							>
								{currentUser.isEmployeeUser && (
									<Grid item xs={12}>
										<CerebroLabel id="lenderGuid">Lender</CerebroLabel>
										{lenderQuery.isLoading && <Skeleton height={60} />}
										{lenderQuery.isSuccess && (
											<>
												<Field
													id="lenderGuid"
													as={CerebroDropdown}
													name="lenderGuid"
													disabled={isSubmitting}
												>
													{lenderQuery.data.map(lender => (
														<CerebroDropdownMenuItem key={lender.guid} value={lender.guid}>
															{lender.fullName} ({lender.bankName})
														</CerebroDropdownMenuItem>
													))}
												</Field>
												<CerebroText variant="hint">
													Leave blank if it is a general request that should be visible to all
													lenders.
												</CerebroText>
											</>
										)}
									</Grid>
								)}

								<Grid item xs={12}>
									<CerebroLabel required id="title">
										Question
									</CerebroLabel>
									<Field
										id="title"
										as={CerebroInput}
										name="title"
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('title', errors, touched)}
									/>
									{FormikUtils.IsInvalid('title', errors, touched) && (
										<CerebroText variant="error">{errors.title}</CerebroText>
									)}
								</Grid>

								<Grid item xs={12}>
									<CerebroLabel id="details">Additional Details / Context</CerebroLabel>
									<Field
										id="details"
										as={CerebroTextarea}
										name="details"
										rows={5}
										disabled={isSubmitting}
										error={FormikUtils.IsInvalid('details', errors, touched)}
									/>
									{FormikUtils.IsInvalid('details', errors, touched) && (
										<CerebroText variant="error">{errors.details}</CerebroText>
									)}
								</Grid>

								{!currentUser.isBankUser && (
									<>
										<Grid item xs={12}>
											<CerebroLabel required id="folderGuid">
												Data Room Folder
											</CerebroLabel>
											{folderQuery.isLoading && <Skeleton height={60} />}
											{folderQuery.isSuccess && (
												<CerebroStyledBox alignitems="left" sx={{ marginTop: 1 }}>
													<CerebroHierarchyFolderTree
														folder={folderQuery.data}
														selectedGuid={values.folderGuid}
														hidePrivateFolders={false}
														onSelected={value => {
															values.folderGuid = value;
															setFieldValue('folderGuid', value);
														}}
													/>
												</CerebroStyledBox>
											)}
										</Grid>
										<Grid item xs={12}>
											<CerebroLabel id="sendEmail">Send Email to Borrower Contacts</CerebroLabel>
											<Field
												id="sendEmail"
												as={CerebroYesNo}
												name="sendEmail"
												disabled={isSubmitting}
											/>
											<CerebroText variant="hint">
												If checked, an email alert will be sent to all borrower contacts.
											</CerebroText>
										</Grid>
									</>
								)}

								<Grid item xs={8}>
									<CerebroPrimaryButton loading={isSubmitting} type="submit">
										Save
									</CerebroPrimaryButton>
								</Grid>
							</Grid>
						</CerebroForm>
					);
				}}
			</Formik>
		</CerebroDrawer>
	);
};

type AddQuestionFormMutationVariables = FormikUtils.MutateOnSubmitVariables<AddQuestionFormValues>;
interface AddQuestionFormValues {
	lenderGuid: string | undefined;
	title: string;
	diligenceType: DiligenceRequestType;
	details: string;
	folderGuid: string;
	sendEmail: boolean;
}

type AddQuestionFormErrors = FormikErrors<AddQuestionFormValues>;

function validateForm(values: AddQuestionFormValues) {
	const errors: AddQuestionFormErrors = {};

	if (!values.title) {
		const title = values.diligenceType == 'Question' ? 'Question' : 'Requested Documents';
		errors.title = `${title} is required`;
	} else {
		if (values.title.length > 255) {
			errors.title = 'Cannot exceed 255 characters';
		}
	}

	if (!values.folderGuid) {
		errors.folderGuid = 'Data room folder is required';
	}

	return errors;
}
